import { useState } from "react";

import { useArmory, useArmoryDispatch } from "../contexts/ArmoryContext";
import {
  BulkAddToInventoryDTO,
  Site,
  InventoryObjectSku,
} from "../apis/armoryApi";
import { useArmoryApi } from "../hooks/useArmoryApi";

import SiteDropdown from "./SiteDropdown";
import AssetSKUDropdown from "./AssetSKUDropdown";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import exp from "constants";
import { Calendar } from "primereact/calendar";

const BulkAddToInventoryPanel: React.FC = () => {
  const { isCursorLoading } = useArmory();
  const dispatch = useArmoryDispatch();
  const { bulkAddToInventory, validateNewSerialNumbers } = useArmoryApi();
  const [site, setSite] = useState<Site | null>(null);
  const [sku, setSku] = useState<InventoryObjectSku | null>(null);
  const [siteSchoolYear, setSiteSchoolYear] = useState<number | null>(null);
  const [purchaseDate, setPurchaseDate] = useState<Date | null>(null);
  const [purchasePrice, setPurchasePrice] = useState<number | null>(null);
  const [serials, setSerials] = useState<string>("");

  const handleSubmit = async () => {
    dispatch({
      type: "SET_VALUE",
      payload: { key: "isCursorLoading", value: true },
    });

    //convert serials to array
    const serialArr = serials.trim().split("\n");

    //validate serials
    const serialResponse = await validateNewSerialNumbers(serialArr);
    if (serialResponse.statusCode === 200) {
      // serials valid, validate values

      // validate site
      if (!site) {
        const errorMsg: string = "Site must be selected.";
        dispatch({
          type: "SHOW_TOAST",
          payload: {
            severity: "error",
            summary: "Error",
            detail: errorMsg,
            life: 3000,
          },
        });
        dispatch({
          type: "SET_VALUE",
          payload: { key: "isCursorLoading", value: false },
        });
        return;
      }

      // validate sku
      if (!sku) {
        const errorMsg: string = "SKU must be selected.";
        dispatch({
          type: "SHOW_TOAST",
          payload: {
            severity: "error",
            summary: "Error",
            detail: errorMsg,
            life: 3000,
          },
        });
        dispatch({
          type: "SET_VALUE",
          payload: { key: "isCursorLoading", value: false },
        });
        return;
      }
      // validate site school year
      if (siteSchoolYear === null) {
        const errorMsg: string = "Site school year must be selected.";
        dispatch({
          type: "SHOW_TOAST",
          payload: {
            severity: "error",
            summary: "Error",
            detail: errorMsg,
            life: 3000,
          },
        });
        dispatch({
          type: "SET_VALUE",
          payload: { key: "isCursorLoading", value: false },
        });
        return;
      }

      // validate purchase date
      if (!purchaseDate) {
        const errorMsg: string = "Purchase date must be selected.";
        dispatch({
          type: "SHOW_TOAST",
          payload: {
            severity: "error",
            summary: "Error",
            detail: errorMsg,
            life: 3000,
          },
        });
        dispatch({
          type: "SET_VALUE",
          payload: { key: "isCursorLoading", value: false },
        });
        return;
      }

      // validate purchase price
      if (purchasePrice === null || purchasePrice < 0) {
        const errorMsg: string = "Purchase price must be a valid number.";
        dispatch({
          type: "SHOW_TOAST",
          payload: {
            severity: "error",
            summary: "Error",
            detail: errorMsg,
            life: 3000,
          },
        });
        dispatch({
          type: "SET_VALUE",
          payload: { key: "isCursorLoading", value: false },
        });
        return;
      }

      // all validations passed, proceed to add to inventory
      // build DTO
      const dto: BulkAddToInventoryDTO = {
        siteId: site!.id,
        assetSkuId: sku!.id,
        siteSchoolYear: siteSchoolYear!,
        purchaseDate: purchaseDate!,
        purchasePrice: purchasePrice!,
        serialNumbers: serialArr,
      };
      // add to inventory
      const response = await bulkAddToInventory(dto);

      if (response.statusCode === 200) {
        const successMsg: string = `Bulk Add to Inventory succeeded.`;
        dispatch({
          type: "SHOW_TOAST",
          payload: {
            severity: "success",
            summary: "Success",
            detail: successMsg,
            life: 3000,
          },
        });
        dispatch({
          type: "ADD_LOG_ENTRY",
          payload: {
            message: `${successMsg}\r\nSerials: ${serialArr.join("\r\n")}`,
          },
        });
        setSerials("");
        setSite(null);
      } else {
        const errorMsg: string = `Bulk Add to Inventory failed. ${response.error}`;
        dispatch({
          type: "SHOW_TOAST",
          payload: {
            severity: "error",
            summary: "Error",
            detail: errorMsg,
            life: 3000,
          },
        });
        dispatch({ type: "ADD_LOG_ENTRY", payload: { message: errorMsg } });
      }
    } else {
      const errorMsg: string = `One or more serial numbers failed to validate. ${serialResponse.error}`;
      dispatch({
        type: "SHOW_TOAST",
        payload: {
          severity: "error",
          summary: "Error",
          detail: errorMsg,
          life: 3000,
        },
      });
      dispatch({ type: "ADD_LOG_ENTRY", payload: { message: errorMsg } });
    }

    setTimeout(() => {
      dispatch({
        type: "SET_VALUE",
        payload: { key: "isCursorLoading", value: false },
      });
    }, 0);
  };

  return (
    <div className="p-fluid">
      <label htmlFor="siteDropdown">Site</label>
      <SiteDropdown
        handleChange={setSite}
        selectedSiteName={site ? site.name : ""}
        disabled={isCursorLoading}
      />
      <label htmlFor="skuDropdown">SKU</label>
      <AssetSKUDropdown
        handleChange={(sku) => setSku(sku)}
        disabled={isCursorLoading}
      />
      <label htmlFor="siteSchoolYear">Site School Year</label>
      <InputNumber
        id="siteSchoolYear"
        value={siteSchoolYear}
        onChange={(e) => setSiteSchoolYear(e.value)}
        disabled={isCursorLoading}
      />
      <label htmlFor="purchaseDate">Purchase Date</label>
      <Calendar
        id="purchaseDate"
        value={purchaseDate}
        onChange={(e) => setPurchaseDate(e.value!)}
        disabled={isCursorLoading}
      />
      <label htmlFor="purchasePrice">Purchase Price</label>
      <InputNumber
        id="purchasePrice"
        value={purchasePrice}
        onValueChange={(e) => setPurchasePrice(e.value!)}
        mode="currency"
        currency="USD"
        locale="en-US"
        disabled={isCursorLoading}
      />
      <InputTextarea
        value={serials}
        onChange={(e) => setSerials(e.target.value)}
        placeholder="Enter or scan serials"
        autoResize={true}
        className="p-mb-3 p-inputtext-sm"
      />
      <Button
        label="Submit"
        onClick={handleSubmit}
        disabled={isCursorLoading}
        className="p-button-rounded p-button-success p-mt-2"
      />
    </div>
  );
};

export default BulkAddToInventoryPanel;